import { create, useStore } from 'zustand';
import { defaultUIStoreState, type FormStatusType, type UIStore, type UIStoreSelector, type UIStoreState } from './types';

function getPlaceholderRegex(key: string) {
  return new RegExp(`{${key}}`, 'g');
}

/**
 * The `createUIStore` function initializes a Zustand store for managing UI state.
 *
 * It combines the default state defined in `defaultUIStoreState` with actions
 * that allow for updating the state. For example, it provides a method
 * `setAuthUIVisibility` to toggle the visibility of the authentication UI.
 *
 * This store is designed to centralize and manage UI-related state in a
 * predictable and scalable way.
 */
export const createUIStore = create<UIStore>()(set => ({
  ...defaultUIStoreState,

  setAuthUIVisibility: (isVisible: boolean) => set({ isAuthUIVisible: isVisible }),

  setContactFormFields: (fields: Partial<Omit<UIStoreState['contactForm'], 'status'>>) =>
    set(state => ({
      contactForm: {
        ...state.contactForm,
        ...fields,
      },
    })),

  setContactFormStatus: (status: FormStatusType) =>
    set(state => ({
      contactForm: {
        ...state.contactForm,
        status,
      },
    })),

  setContactMessageToSample: (orderId: string) =>
    set(state => {
      const sampleMessage = state.contactForm.sampleMessage || '';
      const regex = getPlaceholderRegex('order_id');
      const replacedMessage = sampleMessage.replace(regex, orderId);

      return {
        contactForm: {
          ...state.contactForm,
          text: replacedMessage,
        },
      };
    }),

  setContactFormSubjectToMissingOrder: () =>
    set(state => {
      const subject = state.contactForm.availableSubjects?.find(as => as.id === 'i18n_topic_where_is_my_order');

      return {
        contactForm: {
          ...state.contactForm,
          selectedSubject: subject,
        },
      };
    }),

  setContactFormSubjectToCancellation: () =>
    set(state => {
      const subject = state.contactForm.availableSubjects?.find(
        s => String(s.id) === 'model_customerSupport_topic_cancel_contract',
      );

      return {
        contactForm: {
          ...state.contactForm,
          selectedSubject: subject,
        },
      };
    }),

  resetContactForm: () =>
    set(state => ({
      contactForm: {
        ...defaultUIStoreState.contactForm,
        status: state.contactForm.status,
      },
    })),

  setCancellationErrors: (errors) =>
    set(state => ({
      contactForm: { ...state.contactForm, cancellationErrors: errors },
    })),
}));

/**
 * The `useUIStore` function is a custom hook that allows components to access
 * and interact with the Zustand UI store. By providing a selector function,
 * you can extract specific parts of the store's state or actions, ensuring
 * efficient re-renders by only subscribing to the necessary data.
 *
 * Example usage:
 *
 * ```tsx
 *   const { isAuthUIVisible, setAuthUIVisibility } = useUIStore(store => store);
 * ```
 */
export function useUIStore(selector: UIStoreSelector) {
  return useStore(createUIStore, selector);
}
